import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Heart, MapPin } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=70";
const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u || FALLBACK);

let favCache = null;
export const resetFavCache = () => { favCache = null; };
export const loadFavIds = () => {
  if (!favCache) favCache = api.get("/favorites/ids").then((r) => new Set(r.data.ids)).catch(() => new Set());
  return favCache;
};

function PickCard({ item, canFav, isFav, onToggle }) {
  return (
    <div className="group relative flex items-center gap-3 rounded-2xl border border-border bg-white p-2.5 pr-3 hover:border-orange/50 hover:shadow-card transition-[border-color,box-shadow]" data-testid={`elite-pick-${item.external_id}`}>
      <img src={src(item.image)} alt="" onError={(e) => { e.currentTarget.src = FALLBACK; }} className="h-12 w-12 rounded-xl object-cover shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wide text-orange">{item.item_type === "program" ? "Program" : "School"}</div>
        <div className="text-sm font-bold text-navy truncate">{item.title}</div>
        <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
          {item.location ? <><MapPin className="h-3 w-3 shrink-0" />{item.location}</> : item.subtitle}
        </div>
      </div>
      {canFav && (
        <button onClick={() => onToggle(item)} data-testid={`elite-pick-fav-${item.external_id}`} aria-label={isFav ? "Remove from favorites" : "Save to favorites"}
          className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${isFav ? "bg-orange text-white" : "bg-navy/5 text-navy hover:bg-orange/15"}`}>
          <Heart className="h-4 w-4" fill={isFav ? "currentColor" : "none"} />
        </button>
      )}
      {item.url && (
        <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`elite-pick-link-${item.external_id}`}
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-navy hover:bg-orange text-white text-xs font-bold px-3 py-2 transition-colors">
          See on Elite <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

export function ElitePicks({ items, canFav }) {
  const [favs, setFavs] = useState(new Set());
  useEffect(() => { if (canFav) loadFavIds().then((s) => setFavs(new Set(s))); }, [canFav]);

  const toggle = async (item) => {
    try {
      const { data } = await api.post("/favorites/toggle", { item_type: item.item_type, external_id: item.external_id });
      const next = new Set(favs);
      data.favorited ? next.add(item.external_id) : next.delete(item.external_id);
      setFavs(next);
      favCache = Promise.resolve(next);
      toast.success(data.favorited ? "Added to favorites" : "Removed from favorites");
    } catch (e) {
      toast.error(e.response?.status === 429 ? "Please slow down a moment." : "Something went wrong");
    }
  };

  if (!items?.length) return null;
  return (
    <div className="mt-3 grid gap-2" data-testid="elite-picks">
      {items.map((it) => <PickCard key={it.external_id} item={it} canFav={canFav} isFav={favs.has(it.external_id)} onToggle={toggle} />)}
    </div>
  );
}
